// src/components/PieChart.js
import { useRef } from "react";
import { Chart as Chartjs, registerables } from 'chart.js';
import { Line, Chart } from "react-chartjs-2";
import { getRelativePosition } from 'chart.js/helpers';
import PropTypes from "prop-types";
import moment from "moment";
import 'chartjs-adapter-moment';
import zoomPlugin from "chartjs-plugin-zoom";
import data from "nextus/Maps/HeatMap/data";

Chartjs.register(...registerables);
Chartjs.register(zoomPlugin); // REGISTER PLUGIN

var timeout, current_chart;
var scroll_event_happening = false;

function GradientReader(field, height, pixel) {

  const canvas = document.createElement('canvas');         // create canvas element
  const ctx = canvas.getContext('2d');                     // get context
  var gradientBg = ctx.createLinearGradient(0, 0, 0, height);    // create a gradient

  canvas.width = 1;                                        // 101 pixels incl.
  canvas.height = height;                                  // as the gradient

  if (field == null) {
    gradientBg.addColorStop(0.85, "rgba(38, 124, 204, 0.85)"); //0.2
    gradientBg.addColorStop(0.70, "rgba(131, 180, 226, 0.85)"); //0.7
    gradientBg.addColorStop(0.4, "rgba(239,128,98,0.85)"); //1
    gradientBg.addColorStop(0.1, "rgba(255, 24, 24, 0.85)"); //1
  }
  else {
    field.gradient(gradientBg);
  }

  ctx.fillStyle = gradientBg;                                     // set as fill style
  ctx.fillRect(0, 0, canvas.width, canvas.height);        // draw a single line

  // method to get color of gradient at % position [0, 100]
  //console.log(pixel)
  return ctx.getImageData(0, pixel, 1, 1).data;
}



function GradientMinMaxLineChart({ chartData = [], field = null, min = 0, min_value, max = null, max_value, startTime, endTime, groupSize, setStartTime, setEndTime }) {

  const chartRef = useRef(null);

  const scrollEvent = (chart) => {
    // console.log(timeout);
    current_chart = chart;
    if (typeof timeout != 'undefined') {
      clearTimeout(timeout);
      timeout = undefined;
    }
    scroll_event_happening = true;

    timeout = setTimeout(() => {
      runWheelScrollEvent(current_chart);
      scroll_event_happening = false;
      timeout = undefined;
    }, 200);
  }

  const runWheelScrollEvent = ({ chart }) => {
    var start_time, end_time, group_size;
    let min_limit = moment(0)
    let max_limit = moment();
    // console.log(chart.scales.x.min)
    let min_moment = moment(chart.scales.x.min);
    let max_moment = moment(chart.scales.x.max);

    if (min_moment.isBefore(min_limit)) {
      start_time = min_limit;
    }
    else {
      start_time = min_moment;
    }

    if (max_moment.isAfter(max_limit)) {
      end_time = max_limit;
    }
    else {
      end_time = max_moment;
    }

    setStartTime(start_time);
    setEndTime(end_time);

  };

  const options = {
    layout: {
      padding: 8
    },
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 0
    },
    // interaction: {
    //   mode: 'index',
    //   intersect: false
    // },
    plugins: {
      zoom: {
        zoom: {
          mode: 'x',
          drag: {
            enabled: true,
          },
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          speed: 0.5, sensitivity: 0.5,
          onZoomComplete: scrollEvent,
        },
        limits: {
          x: {
            min: 0,
            max: moment().unix() * 1000,
            minRange: 16000 * 1000,
          }
        },
        legend: {
          position: 'top',
        },
        title: {
          display: false,
          text: field.label
        },
        tooltip: {
          enabled: true,
        }
      },
      legend: {
        labels: {
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            const {
              labels: {
                usePointStyle,
                pointStyle,
                textAlign,
                color
              }
            } = chart.legend.options;

            return chart._getSortedDatasetMetas().map((meta) => {
              const style = meta.controller.getStyle(usePointStyle ? 0 : undefined);
              //const borderWidth = Chart.helpers.toPadding(style.borderWidth);


              try {
                const { chartArea: { top, bottom, height }, scales: { x, y } } = chart;

                if (datasets[meta.index].label == "Average") {
                  var labelFill = "black";
                }
                else {
                  // console.log("max_value", max_value)
                  // console.log("min_value", min_value)
                  if (datasets[meta.index].label == "Upper Range") {
                    var pixel = y.getPixelForValue(max_value)
                  }
                  else {
                    var pixel = y.getPixelForValue(min_value)
                  }

                  // console.log(datasets[meta.index].label, pixel);


                  // console.log(field);
                  var col = GradientReader(field, Math.ceil(height), Math.floor(pixel - top));
                  // console.log(col);

                  var labelFill = `rgba(${col[0]}, ${col[1]}, ${col[2]}, ${col[3] / 255})`;
                }

              }
              catch (e) {
                var labelFill = "black";
              }

              return {
                text: datasets[meta.index].label,
                fillStyle: labelFill,
                fontColor: color,
                hidden: !meta.visible,
                lineCap: style.borderCapStyle,
                lineDash: style.borderDash,
                lineDashOffset: style.borderDashOffset,
                lineJoin: style.borderJoinStyle,
                lineWidth: 1,
                strokeStyle: labelFill,
                pointStyle: pointStyle || style.pointStyle,
                rotation: style.rotation,
                textAlign: textAlign || style.textAlign,
                borderRadius: 0,
                datasetIndex: meta.index
              };
            }, this);
          }
        }
      }
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 5
      }
    },
    scales: {
      x: {
        stacked: true,
        type: 'time',
        min: startTime,
        max: endTime
      },
      y: {
        min: min,
        max: max,
        suggestedMin: min,
        suggestedMax: max,
      }
    }
  };

  var chart = (
    <Line
      ref={chartRef}
      data={chartData}
      options={options}
    />
  )

  return chart;
};

// Typechecking props for the GradientMinMaxLineChart
GradientMinMaxLineChart.propTypes = {
  chartData: PropTypes.object,
  min: PropTypes.number,
  min_value: PropTypes.number,
  max: PropTypes.number,
  max_value: PropTypes.number,
  field: PropTypes.object,
  startTime: PropTypes.instanceOf(moment()),
  setStartTime: PropTypes.func,
  endTime: PropTypes.instanceOf(moment()),
  setEndTime: PropTypes.func,
  groupSize: PropTypes.number,
};


export default GradientMinMaxLineChart;